import { useState, useRef, useCallback } from 'react';
import {
  Box, Stack, Button, IconButton, Typography, Menu, MenuItem,
  ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from '../../../components/iconify';

export default function TabBar({
  tabs = [],
  activeTabId,
  defaultTabId,
  onSelectTab,
  onAddTab,
  onRenameTab,
  onDeleteTab,
  onReorderTabs,
  onSetDefault,
  onOpenSettings,
}) {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTabId, setMenuTabId] = useState(null);
  const [renaming, setRenaming] = useState(null); // tabId being renamed
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // tabId to confirm delete
  const renameRef = useRef(null);

  // Drag state
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleMenuOpen = useCallback((e, tabId) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTabId(tabId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuTabId(null);
  }, []);

  const startRename = useCallback(() => {
    const tab = tabs.find((t) => t.id === menuTabId);
    const tabId = menuTabId;
    setRenameValue(tab?.name || '');
    handleMenuClose();
    // Delay setting renaming state so the Menu's focus-return cleanup
    // finishes before the TextField mounts with autoFocus
    setTimeout(() => setRenaming(tabId), 100);
  }, [menuTabId, tabs, handleMenuClose]);

  const confirmRename = useCallback(() => {
    if (renaming && renameValue.trim()) {
      onRenameTab(renaming, renameValue.trim());
    }
    setRenaming(null);
    setRenameValue('');
  }, [renaming, renameValue, onRenameTab]);

  const handleSetDefault = useCallback(() => {
    onSetDefault(menuTabId);
    handleMenuClose();
  }, [menuTabId, onSetDefault, handleMenuClose]);

  const handleDeleteClick = useCallback(() => {
    setDeleteConfirm(menuTabId);
    handleMenuClose();
  }, [menuTabId, handleMenuClose]);

  const confirmDelete = useCallback(() => {
    if (deleteConfirm) {
      onDeleteTab(deleteConfirm);
    }
    setDeleteConfirm(null);
  }, [deleteConfirm, onDeleteTab]);

  const deleteTabName = tabs.find((t) => t.id === deleteConfirm)?.name || 'this tab';

  // Drag handlers
  const handleDragStart = useCallback((e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((e, toIndex) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== toIndex) {
      onReorderTabs(dragIndex, toIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, onReorderTabs]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', borderBottom: `1px solid ${theme.palette.divider}`, gap: 4, minHeight: 36 }}>
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTabId;
          const isDefault = tab.id === defaultTabId;
          const isRenaming = renaming === tab.id;
          const isDragOver = dragOverIndex === index && dragIndex !== index;

          return (
            <div
              key={tab.id}
              draggable={!isRenaming}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                display: 'flex',
                alignItems: 'center',
                borderLeft: isDragOver ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
              }}
            >
              <div
                role="tab"
                aria-selected={isActive}
                tabIndex={isRenaming ? -1 : 0}
                onClick={() => !isRenaming && onSelectTab(tab.id)}
                onKeyDown={(e) => {
                  if (isRenaming) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectTab(tab.id);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '6px 8px 6px 12px',
                  borderRadius: '8px 8px 0 0',
                  backgroundColor: isActive ? theme.palette.background.paper : 'transparent',
                  borderBottom: isActive ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  cursor: isRenaming ? 'default' : 'pointer',
                  userSelect: 'none',
                }}
              >
                {isDefault && (
                  <Iconify icon="eva:star-fill" width={14} sx={{ color: 'warning.main' }} />
                )}
                {isRenaming ? (
                  <TextField
                    inputRef={renameRef}
                    size="small"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={confirmRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmRename();
                      if (e.key === 'Escape') { setRenaming(null); setRenameValue(''); }
                    }}
                    autoFocus
                    sx={{ width: 100, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.25, px: 0.5 } }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span style={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 400, color: isActive ? theme.palette.text.primary : theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                    {tab.name || 'Untitled'}
                  </span>
                )}
                {!isRenaming && (
                  <IconButton
                    size="small"
                    aria-label={`Tab options for ${tab.name || 'Untitled'}`}
                    onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, tab.id); }}
                    sx={{ p: 0.25, ml: 0.25, opacity: isActive ? 0.6 : 0.3, '&:hover': { opacity: 1 } }}
                  >
                    <Iconify icon="eva:more-vertical-fill" width={14} />
                  </IconButton>
                )}
              </div>
            </div>
          );
        })}

        <IconButton size="small" onClick={onAddTab} title="Add tab" aria-label="Add tab">
          <Iconify icon="eva:plus-fill" width={18} />
        </IconButton>

        <div style={{ marginLeft: 'auto' }}>
          <IconButton size="small" onClick={onOpenSettings} title="Workspace settings" aria-label="Workspace settings">
            <Iconify icon="eva:settings-2-outline" width={18} />
          </IconButton>
        </div>
      </div>

      {/* Kebab menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={startRename}>
          <ListItemIcon><Iconify icon="eva:edit-fill" width={18} /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSetDefault} disabled={menuTabId === defaultTabId}>
          <ListItemIcon><Iconify icon="eva:star-outline" width={18} /></ListItemIcon>
          <ListItemText>{menuTabId === defaultTabId ? 'Default tab' : 'Set as default'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick} disabled={tabs.length <= 1} sx={{ color: 'error.main' }}>
          <ListItemIcon><Iconify icon="eva:trash-2-outline" width={18} sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirm != null} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete Tab</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete "{deleteTabName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This tab's widgets, notes, timeline markers, and rules will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
